import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../components/ui/button';
import AnimatedBackground from '../components/ui/animated-background';

const HowItWorksContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 1rem;
  position: relative;
  overflow: hidden;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradient.text};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.75rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 4rem;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  position: relative;
  z-index: 2;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 5rem;
  margin-bottom: 5rem;
`;

const Step = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  position: relative;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  
  &:nth-child(even) {
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 1.5rem;
    
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const StepContent = styled.div``;

const StepNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradient.cta};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radii.full};
  background-color: rgba(124, 58, 237, 0.1);
  box-shadow: 0 2px 10px rgba(124, 58, 237, 0.1);
`;

const StepTitle = styled.h2`
  font-size: 2.25rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
`;

const StepDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 1.75rem;
  max-width: 90%;
`;

const StepFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StepFeature = styled.li`
  font-size: 1.125rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.radii.md};
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  line-height: 1.4;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.1);
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 22px;
    height: 22px;
    min-width: 22px;
    background: linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
    mask-size: 14px;
    mask-repeat: no-repeat;
    mask-position: center;
    margin-right: 0.75rem;
    border-radius: 50%;
    position: relative;
    top: -1px;
  }
`;

const StepImageWrapper = styled.div`
  border-radius: ${({ theme }) => theme.radii.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  transform: perspective(1000px) rotateY(0deg);
  transition: ${({ theme }) => theme.transitions.default};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%);
    z-index: 1;
    opacity: 0.6;
    transition: ${({ theme }) => theme.transitions.default};
  }
  
  &:hover {
    transform: perspective(1000px) rotateY(-5deg);
    box-shadow: ${({ theme }) => theme.shadows.xl};
    
    &::before {
      opacity: 0.4;
    }
  }
`;

const StepImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  position: relative;
  z-index: 0;
  transition: ${({ theme }) => theme.transitions.default};
  transform: scale(1);
  
  ${StepImageWrapper}:hover & {
    transform: scale(1.05);
  }
`;

const CtaSection = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.radii.lg};
  margin-top: 3rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, rgba(124, 58, 237, 0) 70%);
    z-index: 0;
  }
  
  &::before {
    top: -100px;
    right: -100px;
  }
  
  &::after {
    bottom: -100px;
    left: -100px;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.15) 0%, rgba(236, 72, 153, 0) 70%);
  }
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.gradient.text};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 2;
`;

const CtaDescription = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  position: relative;
  z-index: 2;
`;

const HowItWorksPage: React.FC = () => {
  useEffect(() => {
    document.title = 'How It Works | FylePrep';
  }, []);

  const steps = [
    {
      number: 'Step 01',
      title: 'Simple Document Collection',
      description: 'FylePrep makes gathering tax documents from your clients effortless. No more chasing emails or sorting through paper files.',
      features: [
        'Works with the tax software you already use',
        'Automatically organizes documents by client and tax year',
        'Sends friendly reminders to clients for missing documents',
        'Bank-level security keeps sensitive information safe'
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80'
    },
    {
      number: 'Step 02',
      title: 'Smart Document Review',
      description: 'Our system reads and understands tax documents, highlighting important information so you can focus on what matters most.',
      features: [
        'Automatically extracts key tax information from documents',
        'Flags potential issues before they become problems',
        'Compares current year data with previous years',
        'Reduces review time by up to 60%'
      ],
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80'
    },
    {
      number: 'Step 03',
      title: 'Clear Client Dashboards',
      description: 'Give your clients a simple way to see their tax status, upcoming deadlines, and required documents—all in one place.',
      features: [
        'Easy-to-understand tax summaries for your clients',
        'Progress trackers show exactly what\'s still needed',
        'Automatic updates when documents are received',
        'Mobile-friendly access for clients on the go'
      ],
      image: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80'
    },
    {
      number: 'Step 04',
      title: 'Streamlined Workflow',
      description: 'Complete tax returns faster with organized documents, automated data entry, and a clear view of what needs your attention.',
      features: [
        'Prioritized task lists show what to work on next',
        'Automated follow-ups for missing client information',
        'Team collaboration tools for larger practices',
        'Year-round tax planning tools to better serve clients'
      ],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80'
    }
  ];

  return (
    <HowItWorksContainer>
      <AnimatedBackground />
      <PageTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        How FylePrep Makes Tax Prep Easier
      </PageTitle>
      
      <PageSubtitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Say goodbye to tax season headaches. Our simple four-step process helps CPAs and accounting 
        professionals save time, reduce errors, and delight clients.
      </PageSubtitle>
      
      <ProcessSteps>
        {steps.map((step, index) => (
          <Step key={index}>
            <StepContent>
              <StepNumber>{step.number}</StepNumber>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
              <StepFeatures>
                {step.features.map((feature, featureIndex) => (
                  <StepFeature key={featureIndex}>{feature}</StepFeature>
                ))}
              </StepFeatures>
            </StepContent>
            
            <StepImageWrapper>
              <StepImage src={step.image} alt={step.title} />
            </StepImageWrapper>
          </Step>
        ))}
      </ProcessSteps>
      
      <CtaSection
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <CtaTitle>Ready to Make Tax Season Less Stressful?</CtaTitle>
        <CtaDescription>
          Join hundreds of CPAs who are saving 5-10 hours per week during tax season.
          FylePrep helps you focus on advising clients instead of chasing documents.
        </CtaDescription>
        <Button size="large" variant="primary">Join the Waitlist</Button>
      </CtaSection>
    </HowItWorksContainer>
  );
};

export default HowItWorksPage;
